import React, { useEffect } from "react";
import { defineChatTool, useUIEvents } from "@codenexus/react";
import type { ChatTool, ToolRendererProps } from "@codenexus/react";
import type { AppChatEventMap } from "../types";
import AuthorizeCard from "./AuthorizeCard";
import ProjectsCard from "./ProjectsCard";
import StatusCard from "./StatusCard";
import SyncCard from "./SyncCard";
import {
    configureJiraConnectFlow,
    getJiraConnectState,
    jiraConnectActions,
    setJiraConnectContextSink
} from "./flowStore";

const NO_PARAMS = {
    type: "object",
    properties: {},
    required: [] as string[],
    additionalProperties: false
} as const;

/** Wrap a card so mounting it wires the store's context notes to pushContext.
 *  All four renderers share the same ChatProvider bus, so last-set wins is fine. */
function withContextSink(Card: React.ComponentType): (props: ToolRendererProps) => React.ReactElement {
    return function JiraConnectToolRenderer(_props: ToolRendererProps): React.ReactElement {
        const bus = useUIEvents<AppChatEventMap>();
        useEffect(() => {
            setJiraConnectContextSink(note => bus.pushContext(note));
        }, [bus]);
        return <Card />;
    };
}

function notStarted(): Record<string, unknown> | null {
    if (getJiraConnectState().stage === "idle") {
        return { error: "The Jira connect flow has not started. Call integration_oauth_authorize first." };
    }
    return null;
}

// category "read" on all four: each card's own buttons (Use this account /
// Continue) are the user's explicit consent, so the generic mutation-approval
// prompt would be redundant friction (same rationale as upload_file_to_knowledge_base).
export function createJiraConnectTools(getToken: () => Promise<string>): ChatTool[] {
    configureJiraConnectFlow(getToken);

    const integration_oauth_authorize = defineChatTool<Record<string, never>, AppChatEventMap>({
        name: "integration_oauth_authorize",
        category: "read",
        description:
            "Step 1 of 4 of the Connect Jira flow. Renders the authorization card in chat: the user picks a " +
            "saved Atlassian connection or authorizes in an OAuth popup. Call this first when the user wants " +
            "to connect Jira. Wait for a jira_connect context note with event 'authorized' before calling " +
            "integration_status. Do not claim the connection is ready before the flow's sync_complete note.",
        parameters: NO_PARAMS,
        execute: async () => {
            jiraConnectActions.beginAuthorize();
            return {
                awaiting: true,
                message:
                    "Authorization card displayed. The user completes authorization there. Wait for a " +
                    "jira_connect context note with event 'authorized', then call integration_status."
            };
        },
        Renderer: withContextSink(AuthorizeCard)
    });

    const integration_status = defineChatTool<Record<string, never>, AppChatEventMap>({
        name: "integration_status",
        category: "read",
        description:
            "Step 2 of 4 of the Connect Jira flow. Renders the connection-verification card. Call after " +
            "integration_oauth_authorize's 'authorized' note. Verification runs automatically; wait for the " +
            "'verified' note, then call integration_list_projects.",
        parameters: NO_PARAMS,
        execute: async () => {
            const guard = notStarted();
            if (guard) return guard;
            return {
                awaiting: true,
                stage: getJiraConnectState().stage,
                message: "Verification card displayed. Wait for the jira_connect 'verified' note, then call integration_list_projects."
            };
        },
        Renderer: withContextSink(StatusCard)
    });

    const integration_list_projects = defineChatTool<Record<string, never>, AppChatEventMap>({
        name: "integration_list_projects",
        category: "read",
        description:
            "Step 3 of 4 of the Connect Jira flow. Renders the project-selection card; the user must choose a " +
            "Jira project and press Continue there. Call after the 'verified' note. Wait for the " +
            "'project_linked' note, then call integration_sync.",
        parameters: NO_PARAMS,
        execute: async () => {
            const guard = notStarted();
            if (guard) return guard;
            return {
                awaiting: true,
                stage: getJiraConnectState().stage,
                message:
                    "Project selection card displayed. The user selects a project in the card. Wait for the " +
                    "jira_connect 'project_linked' note, then call integration_sync."
            };
        },
        Renderer: withContextSink(ProjectsCard)
    });

    const integration_sync = defineChatTool<Record<string, never>, AppChatEventMap>({
        name: "integration_sync",
        category: "read",
        description:
            "Step 4 of 4 of the Connect Jira flow — mandatory; the connection is not usable until tickets are " +
            "imported into the Context Mesh. Renders the import card; the import starts automatically once the " +
            "project is linked and shows live progress. Wait for the 'sync_complete' note before telling the " +
            "user the connection is established.",
        parameters: NO_PARAMS,
        execute: async () => {
            const guard = notStarted();
            if (guard) return guard;
            return {
                awaiting: true,
                stage: getJiraConnectState().stage,
                message:
                    "Import card displayed. Ticket import starts automatically after the project link and " +
                    "progress streams into the card. Wait for the jira_connect 'sync_complete' note."
            };
        },
        Renderer: withContextSink(SyncCard)
    });

    return [
        integration_oauth_authorize,
        integration_status,
        integration_list_projects,
        integration_sync
    ] as unknown as ChatTool[];
}
